import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2, ShoppingCart, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { resolveImageUrl } from '../lib/imageUrl';
import { formatPrice } from '../lib/formatPrice';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Doit rester aligné avec MAX_CHAT_HISTORY côté backend
const MAX_HISTORY = 12;

const TEXTS = {
  fr: {
    open: 'Besoin d\'aide ?',
    title: 'Assistant AfriOne',
    subtitle: 'Dites-moi ce qu\'il vous faut',
    placeholder: 'Ex : je veux du riz et de l\'huile de palme',
    welcome: "Bonjour ! Dites-moi ce que vous cherchez et je vous propose les produits. Je peux aussi répondre sur la livraison.",
    add: 'Ajouter',
    addAll: 'Tout ajouter au panier',
    added: 'Ajouté',
    error: "L'assistant est momentanément indisponible. Réessayez dans un instant.",
    close: 'Fermer',
  },
  en: {
    open: 'Need help?',
    title: 'AfriOne Assistant',
    subtitle: 'Tell me what you need',
    placeholder: 'e.g. I want rice and palm oil',
    welcome: 'Hello! Tell me what you are looking for and I will suggest products. I can also answer delivery questions.',
    add: 'Add',
    addAll: 'Add all to cart',
    added: 'Added',
    error: 'The assistant is momentarily unavailable. Please try again shortly.',
    close: 'Close',
  },
};

export default function ChatWidget() {
  const { language } = useLanguage();
  const { addItem } = useCart();
  const t = TEXTS[language] || TEXTS.fr;

  const [enabled, setEnabled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [addedIds, setAddedIds] = useState([]);
  const endRef = useRef(null);

  // L'assistant n'apparaît que s'il est configuré côté serveur
  useEffect(() => {
    axios.get(`${API}/chat/status`)
      .then((r) => setEnabled(Boolean(r.data?.enabled)))
      .catch(() => setEnabled(false));
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleAdd = (product) => {
    addItem(product, product.quantity || 1);
    setAddedIds((ids) => [...ids, product.id]);
    toast.success(t.added, { description: product.name_fr });
  };

  const handleAddAll = (products) => {
    products.forEach((p) => addItem(p, p.quantity || 1));
    setAddedIds((ids) => [...ids, ...products.map((p) => p.id)]);
    toast.success(t.added, { description: `${products.length} produits` });
  };

  const send = async (event) => {
    event.preventDefault();
    const question = input.trim();
    if (!question || loading) return;

    setInput('');
    setLoading(true);
    const nextMessages = [...messages, { role: 'user', content: question }];
    setMessages(nextMessages);

    try {
      // On n'envoie que le texte : les produits proposés restent côté navigateur
      const history = nextMessages
        .slice(-MAX_HISTORY)
        .map(({ role, content }) => ({ role, content }));

      const response = await axios.post(`${API}/chat`, {
        message: question,
        history: history.slice(0, -1),
      });

      setMessages((current) => [...current, {
        role: 'assistant',
        content: response.data.message || '',
        products: response.data.products || [],
      }]);
    } catch (error) {
      setMessages((current) => [...current, { role: 'assistant', content: t.error, products: [] }]);
    } finally {
      setLoading(false);
    }
  };

  if (!enabled) return null;

  return (
    <>
      {/* Bouton flottant - placé au-dessus du bouton WhatsApp */}
      {!isOpen && (
        <motion.button
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-6 z-50 flex items-center gap-2 bg-primary text-white px-4 py-3 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
          data-testid="chat-open-button"
          aria-label={t.open}
        >
          <MessageCircle className="w-5 h-5" />
          <span className="text-sm font-medium hidden sm:inline">{t.open}</span>
        </motion.button>
      )}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-4 right-4 left-4 sm:left-auto sm:bottom-6 sm:right-6 z-50 w-auto sm:w-[400px] max-h-[80vh] flex flex-col bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
            data-testid="chat-panel"
          >
            {/* En-tête */}
            <div className="flex items-center justify-between px-4 py-3 bg-primary text-white">
              <div>
                <p className="font-semibold leading-tight">{t.title}</p>
                <p className="text-xs text-white/80">{t.subtitle}</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
                aria-label={t.close}
                data-testid="chat-close-button"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Conversation */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 min-h-[240px]">
              {messages.length === 0 && (
                <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm text-sm text-text_secondary">
                  {t.welcome}
                </div>
              )}

              {messages.map((message, index) => (
                <div key={index}>
                  <div className={`px-4 py-2.5 text-sm shadow-sm max-w-[85%] ${
                    message.role === 'user'
                      ? 'ml-auto bg-primary text-white rounded-2xl rounded-br-sm'
                      : 'bg-white text-text_primary rounded-2xl rounded-tl-sm'
                  }`}>
                    {message.content}
                  </div>

                  {/* Produits proposés - l'ajout reste une action du client */}
                  {message.products?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {message.products.map((product) => (
                        <div
                          key={product.id}
                          className="flex items-center gap-3 bg-white rounded-xl p-2.5 shadow-sm"
                          data-testid={`chat-product-${product.id}`}
                        >
                          {product.image_url ? (
                            <img
                              src={resolveImageUrl(product.image_url)}
                              alt={product.name_fr}
                              className="w-12 h-12 object-contain rounded-lg bg-gray-50 flex-shrink-0"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-gray-100 flex-shrink-0" />
                          )}

                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-text_primary line-clamp-1">
                              {language === 'fr' ? product.name_fr : (product.name_en || product.name_fr)}
                            </p>
                            <p className="text-xs text-text_secondary line-clamp-1">
                              {[product.brand, product.format].filter(Boolean).join(' · ')}
                            </p>
                            <p className="text-sm font-bold text-primary">
                              {formatPrice(product.price, language)}
                              {product.quantity > 1 && (
                                <span className="text-xs font-normal text-text_secondary"> × {product.quantity}</span>
                              )}
                            </p>
                          </div>

                          <button
                            onClick={() => handleAdd(product)}
                            disabled={addedIds.includes(product.id)}
                            className={`flex items-center gap-1 px-3 py-2 rounded-full text-xs font-semibold transition-all flex-shrink-0 ${
                              addedIds.includes(product.id)
                                ? 'bg-green-100 text-green-700'
                                : 'bg-primary text-white hover:bg-primary_hover'
                            }`}
                          >
                            {addedIds.includes(product.id)
                              ? <><Check className="w-3.5 h-3.5" /> {t.added}</>
                              : <><ShoppingCart className="w-3.5 h-3.5" /> {t.add}</>}
                          </button>
                        </div>
                      ))}

                      {message.products.length > 1 && (
                        <button
                          onClick={() => handleAddAll(message.products)}
                          className="w-full py-2 text-xs font-semibold text-primary border border-primary/30 rounded-full hover:bg-primary/5 transition-colors"
                        >
                          {t.addAll}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {loading && (
                <div className="flex items-center gap-2 text-text_secondary text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* Saisie */}
            <form onSubmit={send} className="p-3 border-t border-gray-100 flex items-center gap-2 bg-white">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t.placeholder}
                maxLength={500}
                disabled={loading}
                className="flex-1 px-4 py-2.5 text-sm rounded-full border border-gray-200 focus:border-primary focus:outline-none disabled:opacity-60"
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-primary text-white hover:bg-primary_hover transition-colors disabled:opacity-40 flex-shrink-0"
                aria-label="Envoyer"
                data-testid="chat-send-button"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
